import React, {useEffect, useRef, useState} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';

import {ReactComponent as RadioCheckedIcon} from '../../../assests/svg/radio-checked.svg';
import {ReactComponent as RadioUnCheckedIcon} from '../../../assests/svg/radio-unchecked.svg';
import AurButton from '../../common/aur-button/AurButton';
import {RootState} from '../../../redux/store';
import {getExportList} from '../../../api/EcaManagementService';
import {
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import AurPagination from '../../common/aur-pagination/AurPagination';
import {AurDataGridColDef} from '../../../types/types';
import {GridEventListener, GridRowParams} from '@mui/x-data-grid';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import AurDataGridLoadingOverlay from '../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import {useNavigate} from 'react-router-dom';
import {setExportId, setExportName} from '../../../redux/slices/addExportSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';

interface TallyDetailsExportDialogProps {
  projectId: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
}

interface Report {
  completedOn: string;
  destination: string;
  documentCount: number;
  exportId: string;
  loadedToDestination: string;
  userName: string;
}

const COLUMN_HEIGHT = 64;
const ROW_HEIGHT = 56;
const TallyDetailsExportDialog: React.FC<TallyDetailsExportDialogProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const {trackGAEvent} = useAnalytics();

  const theme = useTheme();
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [exportReport, setExportReport] = useState<Report[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [totalItems, setTotalItems] = useState<number>();
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [selectedDatasetName, setSelectedDatasetName] = useState<string | null>(
    null
  );
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [exportButtonDisabled, setExportButtonDisabled] =
    useState<boolean>(true);
  const [isFailedToLoadContent, setIsFailedToLoadContent] = useState(false);

  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );
  const {showExportManagement} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const fetchExportReport = async (params: string) => {
    setIsFailedToLoadContent(false);
    try {
      const response = await getExportList(params);
      setExportReport(response?.data?.data);
      setTotalItems(response?.data?.meta?.pagination.totalItems);
    } catch (error) {
      setIsFailedToLoadContent(true);
      console.log(error);
    }
  };

  useEffect(() => {
    setSelectedDataset(null);
    setSelectedDatasetName(null);
    setIsRowSelected(false);
    setExportButtonDisabled(true);
  }, [projectId, isOpen]);

  useEffect(() => {
    if (!projectId || !isOpen) return;
    const paramString = `clientId=${selectedClient.clientId}&projectId=${projectId}&top=${itemsPerPage}&skip=${(currentPage - 1) * itemsPerPage}`;
    fetchExportReport(paramString);
  }, [selectedClient.clientId, projectId, currentPage, itemsPerPage, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setItemsPerPage(25);
      setCurrentPage(1);
    }
  }, [isOpen]);
  const handleExport = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('ecaHomepageModals.exportModal.goToExportManagement'),
        region: GA_REGIONS.MODAL,
        type: GA_CLICK_TYPES.BUTTON,
        action: `${window.location.host}/eci-home-page/export-management`,
      },
      subPage: {
        subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
          label: t('common.exports'),
        }),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    navigate('/eci-home-page/export-management');
  };

  const handleRowClick = (id: string, name: string) => {
    setSelectedDataset(id);
    setSelectedDatasetName(name);
    setIsRowSelected(true);
    setExportButtonDisabled(false);
  };
  const handleRadioChange = (id: string, name: string) => {
    setSelectedDataset(id);
    setSelectedDatasetName(name);
    setExportButtonDisabled(false);
  };

  const viewExportDetailsPage = (exportId: string, exportName: string) => {
    dispatch(setExportId(exportId));
    dispatch(setExportName(exportName));
    navigate('/users/exportdetails');
  };

  const handleCellClick: GridEventListener<'cellClick'> = (params) => {
    if (params.field === 'exportName') {
      viewExportDetailsPage(params.row.exportId, params.row.exportName);
    }
  };

  const onViewExportDetails = () => {
    if (selectedDataset) {
      viewExportDetailsPage(selectedDataset, selectedDatasetName ?? '');
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.ecaHomePage.viewExportDetails'),
          region: GA_REGIONS.MODAL,
          type: GA_CLICK_TYPES.BUTTON,
          action: `${window.location.host}/users/exportdetails`,
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
            label: t('gaTracking.ecaHomePage.exports'),
          }),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  const AurDataGridOverlay = () => {
    if (isFailedToLoadContent)
      return (
        <AurDataGridLoadingOverlay
          containerHeight={
            (tableContainerRef.current?.offsetHeight ?? 500) - COLUMN_HEIGHT
          }
        />
      );
    return <div />;
  };

  const renderModalHeader = () => (
    <Typography className="typo-text headline3_size lightWhite-color" id='tally-details-export-title'>
      {t('ecaHomepageModals.exportModal.heading')}
    </Typography>
  );

  const renderHeaderActions = () => (
    <Box className="header-alignment">
      <Box className="modal-count-header">
        <Typography className="typo-text display3_size lightWhite-color">
          {totalItems}
        </Typography>
        <Typography className="typo-text subhead4_size">
          {t('ecaHomepageModals.exportModal.heading')}
        </Typography>
      </Box>
      <Box>
        {showExportManagement && (
          <AurButton
            data-testid="export-button"
            onClick={handleExport}
            type="continue"
            size="Medium"
            label={t('ecaHomepageModals.exportModal.goToExportManagement')}
            classNames="pagination-continue-button"
          />
        )}
      </Box>
    </Box>
  );

  const columns: AurDataGridColDef[] = [
    {
      field: 'radio',
      headerName: '', // Empty header for the radio column
      sortable: false,
      headerClassName: 'no-separator',
      flex: 0.2,
      renderCell: (params) => {
        const isSelected = selectedDataset === params.row.exportId;
        return (
          <div className="row-container">
            <div
              role="radio"
              className="img-container"
              style={{cursor: 'pointer'}}
              aria-label={`radio button ${params.row.exportName}`}
              aria-checked={isSelected}
              onClick={() =>
                handleRadioChange(params.row.exportId, params.row.exportName)
              }
            >
              {isSelected ? <RadioCheckedIcon /> : <RadioUnCheckedIcon />}
            </div>
          </div>
        );
      },
    },
    {
      field: 'exportName',
      headerName: t('eca.exportManagement.exportName'),
      sortable: false,
      flex: 1.5,
      renderCell: (params) => {
        const promotionNumber = params?.row?.promotionNumber?.trim() || '--';
        return (
          <>
            <Typography className="typo-text body3_size text-color-lightBlue_400">
              {params.value}
            </Typography>
            {params.row?.exportType?.toLowerCase() !== 'analytics' && (
              <AurTooltip
                title={promotionNumber !== '--' ? promotionNumber : ''}
              >
                <Typography className="typo-text body4_size text-color-A0AEC2 space-b line-height-space">
                  {t('common.promotionNumber')}: {promotionNumber}
                </Typography>
              </AurTooltip>
            )}
          </>
        );
      },
    },
    {
      field: 'exportDateTime',
      headerName: t('ecaHomepageModals.exportModal.table.completedOn'),
      width: 180,
      sortable: false,
      isDateColumn: true,
      renderCell: (params) => {
        return (
          <Typography className="typo-text body4_size space-date">
            {getUTCDateTimeForDisplay(params.value)}
          </Typography>
        );
      },
    },
    {
      field: 'destination',
      headerName: t('ecaHomepageModals.exportModal.table.destination'),
      sortable: false,
      flex: 0.7,
      renderCell: () => 'Relativity',
    },
    {
      field: 'exportedBy',
      headerName: t('ecaHomepageModals.exportModal.table.userName'),
      sortable: false,
      flex: 0.7,
    },
    {
      field: 'totalDocExported',
      headerName: t('ecaHomepageModals.exportModal.table.documentCount'),
      sortable: false,
      flex: 0.6,
      renderCell: (params) => {
        return (
          <Typography className="typo-text body3_size">
            {formatNumberWithComma(params.value)}
          </Typography>
        );
      },
    },
    {
      field: 'loadedOnTime',
      headerName: t('ecaHomepageModals.exportModal.table.loadedToDestination'),
      sortable: false,
      flex: 0.8,
      isDateColumn: true,
      renderHeader: () => {
        return (
          <div className="custom-header">
            <span>{t('ecaHomepageModals.exportModal.loadedTo')}</span>
            <br />
            <span>{t('ecaHomepageModals.exportModal.table.destination')}</span>
          </div>
        );
      },
      renderCell: (params) => {
        const formattedDate = params.value
          ? getUTCDateTimeForDisplay(params.value)
          : '-';

        return (
          <Typography className="typo-text body4_size space-date">
            {formattedDate}
          </Typography>
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const renderTable = () => {
    return (
      <div
        tabIndex={0}
        className={`table-container tally-data-container show-scrollbar ${!isFailedToLoadContent ? 'flex-one' : ''}`}
      >
        <AurDataGrid
          className={`listing-data-table align-export-row ${isFailedToLoadContent && 'listing-data-table-fix-height'}`}
          rows={exportReport}
          columns={columns}
          paginationMode="server"
          visibleToolTip={true}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          getRowId={(row) => row.exportId}
          showPagination={false}
          checkBoxVisible={false}
          enableRowClickOnEnterPress
          rowSelection={isRowSelected}
          onRowClick={(params: GridRowParams) =>
            handleRowClick(params.row.exportId, params.row.exportName)
          }
          onCellClick={handleCellClick}
          rowSelectionModel={selectedDataset ? [selectedDataset] : []}
          getRowHeight={() => ROW_HEIGHT}
          columnHeaderHeight={COLUMN_HEIGHT}
          noRowsOverlay={AurDataGridOverlay}
          addedInDialog
          classNameOfParentScollElement='table-container'
        />
      </div>
    );
  };

  const renderDialogActions = () => (
    <DialogActions className="footer-action-container remove-pagination-status">
      <AurPagination
        totalItems={totalItems || 0}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        perPageOptions={TABLE_PAGE_SIZE_OPTION}
        setItemsPerPage={handlePageSizeChange}
        onPageChange={handlePageChange}
      />
      <div className="footer-block">
        <Stack>
          <AurButton
            onClick={onClose}
            type="exit"
            size="Medium"
            label={t('buttons.close')}
            classNames="pagination-cancel-button"
            dataTestId="pagination-cancel-button"
          />
        </Stack>
        <Stack>
          <AurButton
            onClick={onViewExportDetails}
            type="continue"
            size="Medium"
            label={t('ecaHomepageModals.exportModal.viewExportDetails')}
            disabled={exportButtonDisabled}
            classNames="pagination-continue-button"
            dataTestId="pagination-continue-button"
          />
        </Stack>
      </div>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onClose}
      PaperProps={{className: 'evidence-source-dialog'}}
      maxWidth={false}
      aria-labelledby='tally-details-export-title'
    >
      <DialogContent className="model-content">
        {renderModalHeader()}
        {renderHeaderActions()}
        {renderTable()}
      </DialogContent>
      {renderDialogActions()}
      {/* <AurLoadingIndicator overlay={true} isOpen={loading} /> */}
    </Dialog>
  );
};

export default TallyDetailsExportDialog;
